import { memo } from 'react';

import { MenuIcon } from '@air/design-icons';
import { DropdownMenu } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';
import { useBreakpointsContext } from '@air/provider-media-query';

import { usePublicShowcaseNavEntries } from '~/components/v2/components/showcase/hooks/usePublicShowcaseNavEntries';
import { PublicShowcaseNavMenuItem } from '~/components/v2/components/showcase/PublicShowcaseNavMenuItem';
import { PublicShowcaseNavMenuPageEntry } from '~/components/v2/components/showcase/PublicShowcaseNavMenuPageEntry';

/**
 * The mobile treatment of the public navigation chrome: below the medium breakpoint the sticky bar
 * (`PublicShowcaseNav`) is hidden and the same destinations are reached from this hamburger menu,
 * mounted in the workspace header's right-hand region. Above the breakpoint it renders nothing so
 * the two treatments never both appear.
 *
 * The menu (Radix under the primitive) is keyboard-reachable and focus-managed; it inherits the
 * bar's visibility and destinations from the shared hook, so the two never disagree.
 */
export const PublicShowcaseNavMenu = memo(() => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { entries, isVisible } = usePublicShowcaseNavEntries();

  if (!isVisible || isAboveMediumScreen) {
    return null;
  }

  return (
    <DropdownMenu
      align="end"
      data-testid="PUBLIC_COLLECTION_NAV_MENU"
      trigger={
        <IconButton
          adsIcon
          appearance="ghost"
          color="gray"
          data-testid="PUBLIC_COLLECTION_NAV_MENU_TRIGGER"
          icon={MenuIcon}
          label="Showcase pages"
          size="medium"
        />
      }
    >
      {entries.map((entry) =>
        // External links never list boards — they render the plain menu item and skip the board
        // query. Page entries (Home + collection sub-pages) nest their boards indented beneath them.
        entry.isExternal ? (
          <PublicShowcaseNavMenuItem key={entry.id} entry={entry} />
        ) : (
          <PublicShowcaseNavMenuPageEntry key={entry.id} entry={entry} />
        ),
      )}
    </DropdownMenu>
  );
});

PublicShowcaseNavMenu.displayName = 'PublicShowcaseNavMenu';
